import { useEffect, useState, useRef } from "react";
import { ClickAndCollectCountdownUI } from "./ClickAndCollectCountdownUI";
import { ClickAndCollectPreCountdownMessageUI } from "./ClickAndCollectPreCountdownMessageUI";
import { StandardProps } from "../componentMap";
import { ensureIsActiveVariant } from "@/utils/product/ensureIsActive.ts";
import { useVariant } from "@/hooks/useVariant/useVariant.ts";
import { isBetweenDates } from "@/utils/date/isBetweenDates.ts";
import { getColourGroupForVariant } from "@/utils/product/getColourGroupForVariant.ts";
import { Product, ActiveVariant } from "@/types/types";
import { isTwoManDelivery } from "@/utils/product/isTwoManDelivery";
import { DMS_IDS } from "@/constants/constants";

const hideIfDates: [string, string][] = [
  ["2024-12-23", "2024-12-26"],
  ["2024-12-31", "2024-12-31"],
];

const defaultConfig = {
  orderByTime: "19:00",
  collectFromTime: "15:00",
  countdownDurationHours: 3,
  preCountdownDurationHours: 14,
  hideIfWithinLastMinutes: 5,
};

const parseTime = (time: string): { hours: number; minutes: number } => {
  const [h, m] = time.split(":").map((t) => parseInt(t, 10));
  return { hours: h || 0, minutes: m || 0 };
};

const convertToAMPM = (time: string): string => {
  const { hours, minutes } = parseTime(time);
  const hour12 = hours % 12 || 12;
  const suffix = hours >= 12 ? "pm" : "am";
  return minutes === 0
    ? `${hour12}${suffix}`
    : `${hour12}:${minutes.toString().padStart(2, "0")}${suffix}`;
};

const getPriceOrRange = (
  product: Product,
  variant: ActiveVariant,
  isSelected: boolean,
) => {
  if (isSelected) {
    return variant.price.value;
  }

  const variantGroup = getColourGroupForVariant(
    product.variantGroups,
    variant.id,
  );

  const display =
    variantGroup?.variantPriceRange?.display ||
    product.variantPriceRange?.display;

  if (display === undefined) return "";

  return display.max === display.min
    ? display.max.replace("£", "")
    : {
        min: display.min.replace("£", ""),
        max: display.max.replace("£", ""),
      };
};

export function ClickAndCollectCountdown({
  product,
  flags,
  configProps,
}: Pick<
  StandardProps,
  "product" | "runtimeConfig" | "experiencesRawObject" | "flags" | "configProps"
>) {
  const { variant, isSelected } = useVariant(product);
  const [now, setNow] = useState<Date | null>(null);
  const initialLoadTimeRef = useRef(new Date());
  const activeVariant = ensureIsActiveVariant(variant);
  const isShipFromStore = variant.availability?.shipFromStore === true;
  const clickAndCollectCountdownPosition =
    flags.clickAndCollectCountdownPosition?.value || null;
  const configuredPosition =
    configProps?.clickAndCollectCountdownPosition || null;
  const isDeliveryAndGuaranteeUpdatesEnabled =
    flags.deliveryAndGuaranteeUpdates.value === true;

  useEffect(() => {
    setNow(new Date());
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  if (!now) return null;

  if (product.__typename !== "SimpleProduct") return null;
  if (!activeVariant) return null;

  if (isShipFromStore && isSelected) return null;

  if (Array.isArray(hideIfDates)) {
    const shouldHideWithinDates = hideIfDates.some(([startTime, endTime]) =>
      isBetweenDates(startTime, endTime),
    );

    if (shouldHideWithinDates) {
      return null;
    }
  }

  if (
    configuredPosition &&
    clickAndCollectCountdownPosition != configuredPosition
  ) {
    return null;
  }

  const { availability } = activeVariant;

  if (
    (!availability.availableToOrder ||
      availability.futureAvailability ||
      availability.preOrder) &&
    isSelected
  )
    return null;

  const clickAndCollectFulfilmentOption =
    product.fulfilmentOptions.available.find(
      (option) => option.aliases?.dmsId === DMS_IDS.ClickAndCollect,
    );

  if (!clickAndCollectFulfilmentOption || isTwoManDelivery(product))
    return null;

  if (!product.variants?.some((v) => v.availability?.availableToOrder)) {
    return null;
  }

  const orderByLocalTimeToday =
    clickAndCollectFulfilmentOption.earliestFulfilment?.orderByLocalTimeToday ||
    defaultConfig.orderByTime;
  const collectFromLocalTimeTomorrow = convertToAMPM(
    clickAndCollectFulfilmentOption.earliestFulfilment
      ?.collectFromLocalTimeTomorrow || defaultConfig.collectFromTime,
  );
  const { hours, minutes } = parseTime(orderByLocalTimeToday);

  const cutoffTime = new Date();
  cutoffTime.setHours(hours, minutes, 0, 0);

  const timeLeftMs = cutoffTime.getTime() - now.getTime();

  const countdownDurationMs =
    defaultConfig.countdownDurationHours * 60 * 60 * 1000;
  const preCountdownDurationMs =
    defaultConfig.preCountdownDurationHours * 60 * 60 * 1000;
  const cutoffDelayMs =
    (defaultConfig.hideIfWithinLastMinutes ?? 5) * 60 * 1000;
  const userLoadedTooLate =
    cutoffTime.getTime() - initialLoadTimeRef.current.getTime() < cutoffDelayMs;

  const showCountdown =
    timeLeftMs > 0 && timeLeftMs <= countdownDurationMs && !userLoadedTooLate;

  const showPreCountdownMessage =
    timeLeftMs > 0 &&
    timeLeftMs <= preCountdownDurationMs &&
    timeLeftMs > countdownDurationMs;

  return (
    <>
      {flags.showClickAndCollectPreCountdown.value === true &&
        showPreCountdownMessage && (
          <ClickAndCollectPreCountdownMessageUI
            priceOrRange={getPriceOrRange(product, activeVariant, isSelected)}
            deliveryPrice={clickAndCollectFulfilmentOption.price.display}
            collectFromLocalTimeTomorrow={collectFromLocalTimeTomorrow}
            isDeliveryAndGuaranteeUpdatesEnabled={
              isDeliveryAndGuaranteeUpdatesEnabled
            }
          />
        )}
      {flags.showClickAndCollectCountdown.value === true && showCountdown && (
        <ClickAndCollectCountdownUI
          collectFromLocalTimeTomorrow={collectFromLocalTimeTomorrow}
          timeLeftMs={timeLeftMs}
        />
      )}
    </>
  );
}
